import { ToastAlertTypes, UI } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import * as NB from 'native-base';
import React from 'react';
import * as zod from 'zod';

import { TableContent, TableContentItemColumn } from '../../../../../components/TableContent';
import { TableHeadItemRecurse } from '../../../../../components/TableHead';
import { FiltersToApplyProps } from '../../../../../hooks/useFilterTransformer';
import { NormalizedDistributionProduct } from '../../../../../store/managementAnalysis/types';

export const getYearOrDefault = (year: string) => {
  return year ? parseInt(year, 10) : new Date().getFullYear();
};

export interface ProductTableProps {
  filtersToApply: FiltersToApplyProps;
  searchTerm?: string;
  product: NormalizedDistributionProduct;
  invalidColumns?: boolean;
  dataLength: number;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setDataLength: React.Dispatch<React.SetStateAction<number>>;
}

export type SearchFormData = zod.infer<typeof formSearchSchema>;

export const formSearchSchema = zod.object({
  searchTerm: zod
    .string({ required_error: 'Campo obrigatório' })
    .min(2, 'Campo deve ter no mínimo 2 caracteres')
    .refine((value) => value.trim() !== '', {
      message: 'Campo não pode consistir apenas de espaços em branco',
    }),
});

export const defaultErrorMessage: [ToastAlertTypes.Type, string, string] = [
  'error',
  'Opa! Algo parece errado.',
  'Ocorreu um erro ao carregar o Portal,\nverifique sua conexão com a internet e\ntente novamente em instantes.',
];

export const defaultPagination = { page: 1, limit: 40 };

export const messageError = '--';

export interface LoadingTableProps {
  items?: number;
  columnsItems: TableHeadItemRecurse[];
}

export const LoadingTable: React.FC<LoadingTableProps> = ({ items = 6, columnsItems }) => {
  return (
    <NB.VStack space={Spacing['stack.nano']}>
      {Array.from({ length: items }).map((_, n) => (
        <TableContent
          key={n}
          direction="row"
          space={0}
          py={Spacing['stack.xxs']}
          px={Spacing['inline.xxxs']}>
          {columnsItems.map((columnItem, m) => (
            <TableContentItemColumn key={m.toString()} flex={columnItem.flex}>
              <ColoredSkeleton w="2/3" h="14px" rounded="full" />
            </TableContentItemColumn>
          ))}
        </TableContent>
      ))}
    </NB.VStack>
  );
};

export const ClearTable = () => (
  <NB.VStack alignItems="center" paddingY={Spacing['stack.sm']}>
    <NB.VStack alignItems="center" space={Spacing['stack.quarck']}>
      <UI.Text color="neutral.black">Você pode buscar um produto sempre que precisar.</UI.Text>
      <UI.Text color="neutral.black">Digite o nome do produto no campo de busca.</UI.Text>
    </NB.VStack>
  </NB.VStack>
);

export const NotFoundTable = () => (
  <NB.VStack alignItems="center" paddingY={Spacing['stack.lg']}>
    <NB.VStack alignItems="center" space={Spacing['stack.quarck']}>
      <UI.Illustration name="NotFoundIllustration" testId="empty-illustration" />
      <UI.Text color="neutral.black" variant="semibold">
        Nenhum resultado corresponde à sua pesquisa
      </UI.Text>
      <UI.Text color="neutral.black">Busque por outros filtros e tente novamente.</UI.Text>
    </NB.VStack>
  </NB.VStack>
);

export const ColoredSkeleton = (props: NB.ISkeletonProps) => (
  <NB.Skeleton {...props} endColor={Color['brand.primary.lightest']} />
);
